/**
 * User-facing copy for the `error` query param the backend appends when
 * redirecting back to the frontend after a failed SSO flow.
 *
 * Login flow: GET /api/v1/sso/microsoft/login/callback redirects to
 * `/login?error=<code>` on failure.
 *
 * Admin-consent flow: the "connect directory" flow redirects to
 * `/settings/sso?error=<code>` on failure.
 */

// NOTE: the `error` query param is a bare code (e.g. `sso_not_connected`) with
// no provider field alongside it, so these messages are shared across both
// Microsoft and Google login flows. Codes that were originally worded to name
// Microsoft specifically (e.g. `sso_not_connected`) have been generalized below
// so the copy reads correctly regardless of which provider the user attempted.
// `sso_not_workspace_account` is Google-specific (no Microsoft equivalent) and
// gets its own entry.
const SSO_LOGIN_ERROR_MESSAGES: Record<string, string> = {
  sso_cancelled: 'Sign-in was cancelled.',
  sso_not_connected: 'Single sign-on isn\'t set up for this organization yet.',
  sso_missing_email:
    'Your account doesn\'t have an email address available. Contact your admin.',
  sso_not_allowed:
    'Your account isn\'t authorized to sign in to this workspace. Contact your admin.',
  sso_account_inactive: 'Your account is inactive. Contact your admin.',
  sso_not_workspace_account: 'Sign in with a Google Workspace account, not a personal Google account.',
};

export const getSsoLoginErrorMessage = (error: string | null | undefined): string | null => {
  if (!error) return null;
  return SSO_LOGIN_ERROR_MESSAGES[error] ?? 'Sign-in failed. Please try again.';
};

const SSO_SETTINGS_ERROR_MESSAGES: Record<string, string> = {
  consent_denied: 'Admin consent was declined, so SSO wasn\'t connected.',
  directory_already_connected:
    'This directory is already connected to another EZ AP workspace.',
  not_workspace_account:
    'This isn\'t a Google Workspace account, so it can\'t be used to connect directory access.',
  insufficient_scope:
    'Connected, but the OAuth app didn\'t grant directory access. Check that GOOGLE_SSO_CLIENT_ID points to a dedicated OAuth client with admin.directory.user.readonly configured on its consent screen (not the mailbox-ingestion client).',
};

export const getSsoSettingsErrorMessage = (error: string | null | undefined): string | null => {
  if (!error) return null;
  return SSO_SETTINGS_ERROR_MESSAGES[error] ?? 'Failed to connect SSO. Please try again.';
};
